import type { Clause, ClauseOp, PatternBased, PatternBranch, PatternMeasure, RuleBased } from '@kkp-fraud/shared';
import { branchesOf, isExpressionRow, needsPatternBranches, patternBranchesOf } from './branches';
import { conditionFields, expressionRowCode, expressionRowText } from './expressions';
import type { FieldName } from './fields';
import { formatWindow } from './format';
import { t } from './i18n';

/**
 * The two readback lines under every rule: the `IF … THEN` code line from the rule-intake
 * slides, and the same rule as an English sentence.
 *
 * Ported from `writeSentence()` / `syncRow()` in `docs/console-mockup/app.js` and
 * `summaryBar()` in `build.mjs`. The mockup writes Thai; this writes English, because
 * PLAN-v2 §1.1 makes English the console default and the Thai catalogue is being produced
 * by the i18n team — a hand-written Thai string here would be a key they cannot find.
 */

/** The operator as it appears in the code line — the form the intake slides use. */
const OP_CODE: Record<ClauseOp, string> = {
  eq: '=',
  neq: '<>',
  gt: '>',
  gte: '>=',
  lt: '<',
  lte: '<=',
  in: 'IN',
  nin: 'NOT IN',
  between: 'BETWEEN',
  in_set: 'IN LIST',
  not_in_set: 'NOT IN LIST',
};

/** The operator read aloud. */
const OP_WORDS: Record<ClauseOp, string> = {
  eq: 'is',
  neq: 'is not',
  gt: 'is more than',
  gte: 'is at least',
  lt: 'is less than',
  lte: 'is at most',
  in: 'is one of',
  nin: 'is not one of',
  between: 'is between',
  in_set: 'is in Master list',
  not_in_set: 'is not in Master list',
};

function codeValue(value: unknown): string {
  if (Array.isArray(value)) return `(${value.join(', ')})`;
  if (typeof value === 'string') return `'${value}'`;
  return String(value);
}

function spokenValue(clause: Clause): string {
  const { op, value } = clause;
  if (op === 'between' && Array.isArray(value)) return `${value[0]} and ${value[1]}`;
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

function codeValueOf(clause: Clause): string {
  // A Master list id is a name, not a string literal: `merchant_id IN LIST MERCHANT_WATCHLIST`.
  if ((clause.op === 'in_set' || clause.op === 'not_in_set') && typeof clause.value === 'string') return clause.value;
  return codeValue(clause.value);
}

function clauseCode(clauses: readonly Clause[]): string {
  return clauses
    .map((c, i) => `${i === 0 ? '' : ` ${(c.logic ?? 'AND').toUpperCase()} `}${String(c.field)} ${OP_CODE[c.op]} ${codeValueOf(c)}`)
    .join('');
}

/** `IF fraud_score > 700 AND mcc NOT IN (5411, 5541, 4111) THEN DECLINE` */
export function ruleCodeLine(rule: RuleBased | PatternBased): string {
  if ('clauses' in rule) {
    const clauses = rule.clauses ?? [];
    if (clauses.length === 0) return `IF … THEN ${rule.action}`;
    return `IF ${clauses
      .map((c, i) => `${i === 0 ? '' : ` ${(c.logic ?? 'AND').toUpperCase()} `}${String(c.field)} ${OP_CODE[c.op]} ${codeValueOf(c)}`)
      .join('')} THEN ${rule.action}`;
  }

  // 2a expressions — row 1 an expression row: its conditions in the engine's notation
  const first = patternBranchesOf(rule)[0];
  if (first && isExpressionRow(first)) return `IF ${expressionRowCode(first)} THEN ${first.action}`;
  // 2c rule sets — `set_fired:<set id>`
  const agg = `${rule.aggregate}(${rule.event}${rule.ruleSetId ? `:${rule.ruleSetId}` : ''}${rule.aggregateField ? ` · ${String(rule.aggregateField)}` : ''})`;
  const filters = rule.filters ?? [];
  const where = filters.length > 0 ? ` WHERE ${clauseCode(filters)}` : '';
  return `for the same ${rule.scope}, IF ${agg} in trailing ${formatWindow(rule.windowSeconds)} >= ${rule.threshold}${where} THEN ${rule.action}`;
}

/**
 * The same rule as a sentence a fraud analyst reads without knowing the schema:
 * "If the scheme fraud score is more than 700 and the merchant category is not one of
 * 5411, 5541, 4111 → decline the transaction."
 */
export function ruleSentence(rule: RuleBased | PatternBased): string {
  if ('clauses' in rule) {
    const rows = branchesOf(rule);
    if (rows.length > 1) {
      // One sentence per action row, in order: "If a → decline; otherwise if b → approve."
      const parts = rows.map((row, i) => {
        const one = ruleSentence({ ...rule, action: row.action, clauses: row.clauses, branches: undefined }).replace(/\.$/, '');
        return i === 0 ? one : one.replace(/^If /, 'if ');
      });
      return `${parts.join(`; ${t('rule.sentence.otherwise')} `)}.`;
    }
  } else {
    const rows = patternBranchesOf(rule);
    if (needsPatternBranches(rows)) {
      const parts = rows.map((row, i) => {
        const one = patternRowSentence(row);
        return i === 0 ? one : one.replace(/^If /, 'if ');
      });
      return `${parts.join(`; ${t('rule.sentence.otherwise')} `)}.`;
    }
  }

  const outcome = ACTION_WORDS[rule.action] ?? rule.action;

  if ('clauses' in rule) {
    const clauses = rule.clauses ?? [];
    if (clauses.length === 0) return `This rule has no conditions yet, so it never fires.`;
    const body = clauses
      .map((c, i) => {
        const join = i === 0 ? '' : `${(c.logic ?? 'AND').toUpperCase() === 'OR' ? 'or ' : 'and '}`;
        return `${join}${String(c.field)} ${OP_WORDS[c.op]} ${spokenValue(c)}`;
      })
      .join(' ');
    return `If ${body} → ${outcome}.`;
  }

  return `${patternRowSentence({ action: rule.action, measures: [rule] })}.`;
}

/** "the same card_id reaches 3 approved events within 10 min, counting only those where …" */
function measurePhrase(m: PatternMeasure): string {
  // 2c rule sets — "set_fired:cnp-risk events"
  const event = m.ruleSetId ? `${m.event}:${m.ruleSetId}` : m.event;
  const what = m.aggregate === 'count' ? `${event} events` : `${m.aggregate} of ${String(m.aggregateField ?? '?')} over ${event} events`;
  const filters = m.filters ?? [];
  const only =
    filters.length > 0
      ? `, counting only those where ${filters
          .map((c, i) => `${i === 0 ? '' : 'and '}${String(c.field)} ${OP_WORDS[c.op]} ${spokenValue(c)}`)
          .join(' ')}`
      : '';
  return `the same ${m.scope} reaches ${m.threshold} ${what} within ${formatWindow(m.windowSeconds)}${only}`;
}

/** One pattern row, no final period: "If <measure>, and <measure> → outcome". */
function patternRowSentence(row: PatternBranch): string {
  const outcome = ACTION_WORDS[row.action] ?? row.action;
  // 2a expressions — the conditions read back in the active language
  if (isExpressionRow(row)) return `If ${expressionRowText(row)} → ${outcome}`;
  const join = row.logic === 'OR' ? ', or ' : ', and ';
  return `If ${row.measures.map(measurePhrase).join(join)} → ${outcome}`;
}

const ACTION_WORDS: Record<string, string> = {
  DECLINE: 'decline the transaction',
  STEP_UP: 'challenge the cardholder (3-D Secure, PIN or in-app confirmation)',
  APPROVE_NOTIFY: 'approve and push a Dime! notification',
  ALERT_ONLY: 'approve and open a case for the fraud team',
  APPROVE: 'approve',
};

/**
 * Every field a rule depends on — conditions of every action row, the `requires` guard,
 * and for a pattern rule every measure's scope, filters and aggregate field. De-duplicated, because RB-01 names `fraud_score` in both `requires` and
 * a clause and would otherwise be reported twice.
 *
 * This list is what decides which sources a rule can run on.
 */
export function fieldsUsedBy(rule: RuleBased | PatternBased): FieldName[] {
  const names = new Set<string>();
  if ('requires' in rule) for (const f of rule.requires ?? []) names.add(String(f));
  if ('clauses' in rule) for (const row of branchesOf(rule)) for (const c of row.clauses ?? []) names.add(String(c.field));
  if ('filters' in rule) {
    for (const row of patternBranchesOf(rule)) {
      for (const f of conditionFields(row)) names.add(f); // 2a expressions
      for (const m of row.measures) {
        names.add(String(m.scope));
        for (const c of m.filters ?? []) names.add(String(c.field));
        if (m.aggregateField) names.add(String(m.aggregateField));
      }
    }
  }
  return [...names] as FieldName[];
}
